import { useCallback, useMemo } from 'react';
import {
  GetThreadDetailsResponse,
  GetUnifiedThreadDetailsResponse,
} from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { ValidNumberStatus } from '@weave/schema-gen-ts/dist/schemas/sms/number/v1/number_service.pb';
import { ThreadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { useQueryClient } from 'react-query';
import { Compulsory } from 'ts-toolbelt/out/Object/Compulsory';
import { PersonsV3 } from '@frontend/api-person';
import { SMSDataSharedUtils, SMSDataV4 } from '@frontend/api-sms-data';
import { SMSNumberV1 } from '@frontend/api-sms-number';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { InboxUtilsHooks } from '@frontend/inbox-api-utils';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { useMiniChatShallowStore } from '@frontend/mini-chat-store';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { SchemaPersonV3Service } from '@frontend/schema';
import { useAppScopeStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { usePreferredOutboundNumberUserSettings } from '@frontend/use-preferred-outbound-number-user-settings';
import { useAlert } from '@frontend/design-system';
import { Conversation, GetPersonIO } from './types';
import {
  orderConversationsByPriority,
  resolveHighestPriorityConversation,
  resolveLocationPhones,
  resolvePersonPhones,
  resolveThreadGroupId,
} from './utils';

export type ThreadContextAndOptions = {
  groupIds?: string[];
  threadGroupId?: string;
  threadId?: string;
  personId?: string;
  personPhone?: string;
  locationPhone?: string;
  departmentId?: string;
  focusThreadItemData?: {
    id: string;
    createdAt: string;
  };
  openInInbox?: boolean;
  onSuccess?: () => void;
  onError?: () => void;
  replace?: boolean;
  isNew?: boolean;
};

type OpenThreadArgs = Compulsory<ThreadContextAndOptions, 'threadGroupId' | 'threadId'>;

type UseMessageActionResults = {
  openThread: (args: OpenThreadArgs) => Promise<void>;
  onClick: (args: ThreadContextAndOptions) => Promise<void>;
};

export const useMessageAction = (): UseMessageActionResults => {
  const { t } = useTranslation('inbox');
  const { error: alertError } = useAlert();
  const queryClient = useQueryClient();
  const { navigateToThread } = useInboxNavigate();
  const { addChat } = useMiniChatShallowStore('addChat');
  const { selectedLocationIds } = useAppScopeStore();
  const locationIdsWithACL = useMemo(
    () =>
      filterLocationIdsByACLs({
        locationIds: selectedLocationIds,
        acls: Permission.SMS_INBOX_MANAGE,
      }),
    [selectedLocationIds]
  );
  const {
    data: { allLocationPhones },
    enabledData: { allLocationPhones: enabledLocationPhones },
  } = InboxLocationPhones.Hooks.useLocationPhones();
  const checkLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(locationIdsWithACL);
  const { getPreferredOutboundNumberForLocation } = usePreferredOutboundNumberUserSettings();
  const { getDataFromDepartmentId } = InboxUtilsHooks.useDepartmentLocationPhoneMappings();
  const { getQueryKey: getNumberValidityQueryKey } = SMSNumberV1._QueryUpdaters.useQueryUpdaters();
  const getPhoneValidities = useCallback(
    async (personPhones: string[]) => {
      const responses = await Promise.allSettled(
        personPhones.map(async (personPhone) => {
          const request: SMSNumberV1.Types.GetValidityIO['input'] = {
            phoneNumber: formatPhoneNumberE164(personPhone),
          };
          return queryClient.fetchQuery<SMSNumberV1.Types.GetValidityIO['output']>({
            queryKey: getNumberValidityQueryKey({
              endpointName: 'GetValidity',
              request,
            }),
            queryFn: () => SMSNumberV1.SchemaSMSNumberV1Service.GetValidity(request),
          });
        })
      );

      return responses.map((response, index) => ({
        personPhone: formatPhoneNumberE164(personPhones[index] ?? ''),
        isValid:
          response.status === 'fulfilled'
            ? response.value.valid !== ValidNumberStatus.VALID_NUMBER_STATUS_INVALID
            : true,
      }));
    },
    [queryClient, getNumberValidityQueryKey]
  );

  const { getThreadPersonPhone, isUnifiedThread } = SMSDataSharedUtils.threadItemsUtils;

  const fetchUnifiedThreadDetails = SMSDataV4.Queries.useFetchGetUnifiedThreadDetails();
  const lookupThreadIdV4 = SMSDataV4.Queries.useFetchGetThreadId();
  const fetchConversationsV4 = SMSDataV4.Queries.useFetchBatchGetThreadStatus();

  const fetchPerson = useCallback(
    async (request: GetPersonIO['input']) =>
      queryClient.fetchQuery<GetPersonIO['output']>({
        queryKey: PersonsV3.PersonQueries.queryKeys.getPersonLegacy(request),
        queryFn: () => SchemaPersonV3Service.GetPersonLegacy(request),
      }),
    [queryClient]
  );

  const openThread = useCallback<UseMessageActionResults['openThread']>(
    async ({
      threadGroupId,
      threadId,
      personId,
      personPhone: providedPersonPhone,
      locationPhone: providedLocationPhone,
      departmentId,
      focusThreadItemData,
      openInInbox,
      onSuccess,
      onError,
      replace,
      isNew,
    }) => {
      if (!locationIdsWithACL.length) {
        alertError(t('You do not have permission to send text messages'));
        onError?.();
        return;
      }

      if (!locationIdsWithACL.includes(threadGroupId)) {
        alertError(t('You do not have permission to send text messages from this location'));
        onError?.();
        return;
      }

      if (!threadId || !threadGroupId) {
        alertError(t('Error opening thread.'));
        console.error(
          'Not enough context provided to open thread. Missing data: ' +
            (threadGroupId ? '' : '\n- threadGroupId') +
            (threadId ? '' : '\n- threadId')
        );
        onError?.();
        return;
      }

      let personPhone: string = providedPersonPhone ?? '';
      let locationPhone: string = providedLocationPhone ?? '';
      if (!locationPhone && !!departmentId) {
        locationPhone = getDataFromDepartmentId(departmentId)?.locationPhone ?? '';
      }

      // For new threads (calculated threadId), skip fetching thread details since the thread doesn't exist yet
      if ((!personPhone || !locationPhone) && !isNew) {
        try {
          const threadDetails: GetThreadDetailsResponse | GetUnifiedThreadDetailsResponse =
            await fetchUnifiedThreadDetails({
              request: { threadId, locationId: threadGroupId },
            });

          let fetchedPersonPhone: string | undefined;
          let fetchedLocationPhone: string | undefined;
          const thread = threadDetails?.thread;

          if (isUnifiedThread(thread)) {
            // For unified threads, the thread has items array
            fetchedPersonPhone = thread?.items?.[0] ? getThreadPersonPhone(thread.items[0]) : undefined;
            fetchedLocationPhone = thread?.locationPhone;
          } else {
            // Fallback: thread structure is unknown or thread is null/undefined
            const threadType = thread ? 'unknown structure' : 'null/undefined';
            const errorMessage = `Invalid thread data - thread is ${threadType}. Expected unified thread (with items) or v4 thread (with messages).`;

            sentry.error({
              team: 'fe-messaging',
              topic: 'messages',
              error: errorMessage,
              severityLevel: 'error',
              addContext: {
                name: 'Thread Structure Error',
                context: {
                  threadType,
                  threadId,
                  threadGroupId,
                  thread: thread ? JSON.stringify(thread, null, 2) : null,
                },
              },
            });

            throw new Error(errorMessage);
          }
          if (!fetchedPersonPhone) {
            throw new Error('No person phone on fetched thread');
          }
          if (!fetchedLocationPhone) {
            throw new Error('No location phone on fetched thread');
          }
          personPhone = fetchedPersonPhone;
          locationPhone = fetchedLocationPhone;
        } catch (e) {
          alertError(t('Error looking up conversation. Please try again.'));
          console.error(
            'Not enough context provided to open thread, and looking up thread failed. Missing data: ' +
              (personPhone ? '' : '\n- personPhone') +
              (locationPhone ? '' : '\n- locationPhone')
          );
          sentry.error({
            team: 'fe-messaging',
            topic: 'messages',
            error: 'Error looking up threadDetails',
            severityLevel: 'error',
            addContext: {
              name: 'Network error',
              context: {
                error: e,
              },
            },
          });
          return;
        }
      }

      // Skip location phone access check for new threads where location phone might not be determined yet
      if (locationPhone && !checkLocationPhoneAccess(locationPhone)) {
        alertError(t('You do not have permission to access this conversation.'));
        onError?.();
        return;
      }

      if (openInInbox) {
        onSuccess?.();
        return navigateToThread({
          groupId: threadGroupId,
          threadId,
          personId,
          personPhone,
          locationPhone,
          threadItemId: focusThreadItemData?.id,
          threadItemCreatedAt: focusThreadItemData?.createdAt,
          replace,
          isNew,
        });
      }

      onSuccess?.();
      return addChat({
        groupId: threadGroupId,
        threadId,
        contextPersonId: personId,
        personPhone,
        locationPhone,
        focusThreadItemData,
        unreadCount: 0,
        isNew,
      });
    },
    [addChat, alertError, getDataFromDepartmentId, fetchUnifiedThreadDetails, locationIdsWithACL, navigateToThread, t]
  );

  const onClick = useCallback<UseMessageActionResults['onClick']>(
    async ({
      groupIds: providedGroupIds,
      threadId,
      threadGroupId: providedThreadGroupId = locationIdsWithACL.length === 1 ? locationIdsWithACL[0] : undefined,
      personId,
      personPhone: providedPersonPhone,
      locationPhone: providedLocationPhone,
      departmentId,
      focusThreadItemData,
      openInInbox,
      onSuccess,
      onError,
      replace,
    }) => {
      const groupIds =
        providedGroupIds?.filter((locationId) => locationIdsWithACL.includes(locationId)) ?? locationIdsWithACL;

      const proposedPreferredOutboundNumberForLocation = getPreferredOutboundNumberForLocation(providedThreadGroupId!);

      const availableLocationPhones = resolveLocationPhones({
        allLocationPhones,
        // use the location phone passed into the function, and if not provided, use the preferred outbound number for the location if the feature flag is on
        // this will help not create thread ids for invalid patient phone numbers when opening a thread (i.e. it will hit the single match logic later)
        providedLocationPhone: providedLocationPhone ?? proposedPreferredOutboundNumberForLocation,
        departmentId,
        threadGroupId: providedThreadGroupId,
        groupIds,
        getDataFromDepartmentId,
      });
      const threadGroupId = resolveThreadGroupId({
        providedThreadGroupId,
        groupIds,
        departmentId,
        availableLocationPhones: availableLocationPhones,
        getDataFromDepartmentId,
      });

      if (!!threadId && !!threadGroupId) {
        return openThread({
          groupIds,
          threadId,
          threadGroupId,
          personId,
          personPhone: providedPersonPhone,
          locationPhone: providedLocationPhone,
          departmentId,
          focusThreadItemData,
          openInInbox,
          onSuccess,
          onError,
          replace,
        });
      }

      let availablePersonPhones: string[] = [];
      try {
        const personPhones = await resolvePersonPhones({
          groupIds,
          providedPersonId: personId,
          providedPersonPhone,
          fetchPerson,
          getPhoneValidities,
        });
        availablePersonPhones = [...personPhones];
      } catch (e) {
        alertError(t('Error looking up contact information.'));
        onError?.();
        return;
      }

      if (!availablePersonPhones.length) {
        alertError(
          providedPersonPhone
            ? t('The selected phone number is not a textable phone number.')
            : t('The selected contact has no textable phone numbers.')
        );
        onError?.();
        return;
      }

      // Open thread if exactly one match
      const singleLocationPhone = providedLocationPhone
        ? providedLocationPhone
        : availableLocationPhones.length === 1
        ? availableLocationPhones[0]?.locationPhone
        : undefined;
      const singlePersonPhone = availablePersonPhones.length === 1 ? availablePersonPhones[0] : undefined;
      if (singleLocationPhone && singlePersonPhone && !!threadGroupId) {
        try {
          const response = await lookupThreadIdV4({
            locationId: threadGroupId,
            locationPhone: singleLocationPhone,
            personPhone: singlePersonPhone,
          });
          if (response.threadId && threadGroupId) {
            return openThread({
              threadId: response.threadId,
              threadGroupId,
              personId,
              personPhone: singlePersonPhone,
              locationPhone: singleLocationPhone,
              focusThreadItemData,
              openInInbox,
              onSuccess,
              onError,
              replace,
              isNew: response.calculated,
            });
          }
        } catch (e) {
          alertError(t('Error looking up conversation.'));
          console.error('Error looking up threadId for specified conversation');
          return;
        }
      }

      // Open most recently modified thread depending on phone type (mobile, home, work)
      const orderedConversationsWithAllPersonPhones: Conversation[] = [];
      try {
        const locationPhonesByLocation = availableLocationPhones.reduce<Record<string, string[]>>((acc, curr) => {
          const currentEntries = acc[curr.locationId] ?? [];
          acc[curr.locationId] = [...currentEntries, curr.locationPhone];
          return acc;
        }, {});
        const response = await fetchConversationsV4({
          requests: SMSDataV4.Utils.calculatePossibleThreadStatusRequests({
            personPhones: availablePersonPhones,
            locationPhonesByLocation,
          }),
        });
        const conversations = response.threadStatuses.map<Conversation>(
          ({ personPhone, locationPhone, locationId, threadId, lastModified, status }) => ({
            personPhone,
            locationPhone,
            locationId,
            threadId,
            lastModified: lastModified ?? '',
            status,
          })
        );
        orderedConversationsWithAllPersonPhones.push(
          ...orderConversationsByPriority(conversations, availablePersonPhones)
        );

        const availableOrderedConversationsWithAllPersonPhones = orderedConversationsWithAllPersonPhones.filter(
          (c) =>
            c.status === ThreadStatus.ACTIVE ||
            c.status === ThreadStatus.ARCHIVED ||
            enabledLocationPhones.find((lp) => lp.locationPhone === c.locationPhone)
        );
        const defaultSmsLocationPhone = allLocationPhones.find(
          (phoneData) => phoneData.defaultSms && phoneData.locationId === threadGroupId
        )?.locationPhone;

        let preferredOutboundLocationPhone = undefined; // v3, locationId is not selected, or the preferred number is not available should be undefined
        if (threadGroupId) {
          // Check if the preferred outbound number is in the available location phones
          const isAvailable = availableLocationPhones.find(
            (phoneData) => phoneData.locationPhone === proposedPreferredOutboundNumberForLocation
          );
          if (isAvailable) {
            preferredOutboundLocationPhone = proposedPreferredOutboundNumberForLocation;
          }
        }
        const highestPriorityThread = resolveHighestPriorityConversation({
          conversations: availableOrderedConversationsWithAllPersonPhones,
          defaultSmsLocationPhone,
          preferredOutboundLocationPhone,
        });
        if (!highestPriorityThread) {
          throw new Error('No conversation found for any of the available phone numbers');
        }
        return openThread({
          groupIds,
          threadId: highestPriorityThread.threadId,
          threadGroupId: highestPriorityThread.locationId,
          personId,
          personPhone: highestPriorityThread.personPhone,
          locationPhone: highestPriorityThread.locationPhone,
          focusThreadItemData,
          openInInbox,
          onSuccess,
          onError,
          replace,
          isNew: highestPriorityThread.status === ThreadStatus.NEW,
        });
      } catch (e) {
        alertError(t('Error looking up conversations.'));
        onError?.();
        return;
      }
    },
    [
      alertError,
      allLocationPhones,
      enabledLocationPhones,
      fetchConversationsV4,
      fetchPerson,
      getDataFromDepartmentId,
      getPreferredOutboundNumberForLocation,
      getPhoneValidities,
      locationIdsWithACL,
      lookupThreadIdV4,
      openThread,
      t,
    ]
  );

  return {
    onClick,
    openThread,
  };
};
